"use client";

import React, { useState } from "react";
import { Copy, CircleCheck, Send, Sparkles, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border/50 hover:bg-accent/50 rounded-md">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className="peer pt-6 pb-2 h-14 bg-background/30 border-0 border-b border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-md transition-all" placeholder=" " {...field} />
              </FormControl>
              <FormLabel className="absolute left-3 top-4 text-muted-foreground font-mono text-xs uppercase tracking-wider transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-[10px] pointer-events-none">
                Full Name
              </FormLabel>
              <FormMessage className="pt-1 text-xs text-primary" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input type="email" className="peer pt-6 pb-2 h-14 bg-background/30 border-0 border-b border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-md transition-all" placeholder=" " {...field} />
              </FormControl>
              <FormLabel className="absolute left-3 top-4 text-muted-foreground font-mono text-xs uppercase tracking-wider transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-[10px] pointer-events-none">
                Email Address
              </FormLabel>
              <FormMessage className="pt-1 text-xs text-primary" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Input type="tel" className="peer pt-6 pb-2 h-14 bg-background/30 border-0 border-b border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-md transition-all" placeholder=" " {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 text-muted-foreground font-mono text-xs uppercase tracking-wider transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-[10px] pointer-events-none">
              Phone Number
            </FormLabel>
            <FormMessage className="pt-1 text-xs text-primary" />
          </FormItem>} />

      <FormField control={control} name="project_details" rules={{
      required: "Please describe your legal matter"
    }} render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <Textarea className="peer pt-6 pb-2 min-h-[140px] resize-none bg-background/30 border-0 border-b border-border/50 focus-visible:border-primary focus-visible:ring-0 rounded-md transition-all" placeholder=" " {...field} />
            </FormControl>
            <FormLabel className="absolute left-3 top-4 text-muted-foreground font-mono text-xs uppercase tracking-wider transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-focus:top-1.5 peer-focus:text-[10px] pointer-events-none">
              Case Description
            </FormLabel>
            <FormMessage className="pt-1 text-xs text-primary" />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm bg-destructive/10 border border-destructive/20 text-destructive rounded-md">
          {submitError}
        </div>}

      <div className="space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-base font-mono uppercase tracking-widest bg-primary hover:bg-primary/90 text-primary-foreground transition-all border-0 shadow-lg shadow-primary/10 rounded-md">
          {isSubmitting ? <span className="flex items-center gap-2">
              <Sparkles className="size-5 animate-pulse" />
              Encrypting...
            </span> : <span className="flex items-center gap-2">
              Request Consultation
              <Send className="size-4 ml-1" />
            </span>}
        </Button>
        <div className="flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-primary/70">
          <span className="size-1.5 rounded-lg bg-primary animate-pulse" />
          End-to-End Encrypted Inquiry
        </div>
      </div>
    </Form>;
}
function DirectContact() {
  const [copied, setCopied] = useState(false);
  const email = "counsel@sterlinglaw.com";
  const handleCopy = () => {
    navigator.clipboard.writeText(email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="flex flex-col justify-center h-full space-y-8 p-8 lg:p-12 bg-card/10 rounded-md border border-border/30 backdrop-blur-md relative overflow-hidden group">
      {}
      <div className="absolute -top-24 -right-24 size-48 bg-primary/5 rounded-md blur-3xl group-hover:bg-primary/10 transition-colors duration-700" />
      <div className="absolute -bottom-24 -left-24 size-48 bg-secondary/5 rounded-md blur-3xl group-hover:bg-secondary/10 transition-colors duration-700" />
      
      <div className="relative z-10 space-y-4">
        <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground">
          Direct Counsel
        </h3>
        <p className="text-muted-foreground text-lg font-default leading-relaxed">
          For urgent matters requiring immediate attention, contact our senior associates directly via secure mail.
        </p>
      </div>

      <div className="relative z-10">
        <Button onClick={handleCopy} variant="outline" className="w-full flex items-center justify-between p-4 md:p-6 bg-background/40 hover:bg-accent/20 border border-border/50 rounded-md transition-all duration-300 h-auto">
          <div className="flex items-center gap-4 overflow-hidden">
            <span className="text-2xl md:text-3xl shrink-0"></span>
            <span className="text-lg md:text-xl font-mono font-medium text-foreground truncate">
              {email}
            </span>
          </div>
          <div className="shrink-0 ml-4 size-10 rounded-md bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
            {copied ? <CircleCheck className="size-5" /> : <Copy className="size-5" />}
          </div>
        </Button>
        
        <div className="mt-4 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-muted-foreground">
          <ArrowRight className="size-4" />
          <span>Copy secure address</span>
        </div>
      </div>
    </div>;
}
export default function SecureInquiry() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="247786" id="secure-inquiry" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/office_building_texture.png')] opacity-[0.03] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 rounded-md blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-serif font-bold tracking-tight mb-6">
            Secure <span className="text-primary">Consultation</span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            Initiate a confidential dialogue with Sterling Law Associates. Our team provides elite legal strategy for complex matters.
          </p>
        </div>

        <div ref={contentRef} className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 transition-all duration-1000 delay-200 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-3 bg-card/20 backdrop-blur-xl border border-border/40 rounded-md p-6 md:p-10 shadow-2xl shadow-black/40">
            <div className="mb-8">
              <h3 className="text-2xl font-serif font-semibold mb-2">Case Inquiry</h3>
              <p className="text-muted-foreground font-default">Provide preliminary details for conflict check and review.</p>
            </div>
            
            <FormProvider formKey="secure_inquiry_form_contact" formId="secure_inquiry_form_contact" sectionName="secure-inquiry" defaultValues={{
            name: "",
            email: "",
            phone: "",
            project_details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            project_details: {
              required: true
            }
          }} submitText="Request Consultation" successMessage="Your inquiry has been securely transmitted. A representative will contact you within one business day.">
              <ContactForm />
            </FormProvider>
          </div>

          {}
          <div className="lg:col-span-2">
            <DirectContact />
          </div>
        </div>
      </div>
    </section>;
}